import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { TranslateModule } from '@ngx-translate/core';

import { combineLatest } from 'rxjs';

import { Trip } from 'src/app/shared/classes/trip';
import { Config } from 'src/app/core/configs/config';
import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { ERoutes } from 'src/app/core/enums/routes.enum';
import { PIN_COLORS } from 'src/app/shared/constants/map-pin.constant';

import { SafeUrlPipe } from 'src/app/shared/pipes/safe-url.pipe';
import { GetFavoritesByProvider } from './measure-amount-of-favorites.pipe';

import {
  FavoritesProviderService,
  IFavoriteItem,
} from 'src/app/core/services/favorites-provider.service';
import { CityProviders } from 'src/app/core/utils/city-services';
import { TripsService } from 'src/app/core/services/trips.service';
import { BreakpointService } from 'src/app/core/services/breakpoint.service';
import { CloudinaryService } from 'src/app/core/services/cloudinary.service';
import { UserAccountService } from 'src/app/core/services/user-account.service';
import { HeaderStateService } from 'src/app/core/services/header-state.service';
import { TripWizardStateService } from 'src/app/shared/components/trip-wizard/trip-wizard-state.service';
import { SchedulerBlockNavigationService } from 'src/app/core/services/scheduler-block-navigation.service';

import { HeaderBannerComponent } from 'src/app/shared/components/header-banner/header-banner.component';
import { ViatorGroupComponent } from 'src/app/shared/used-components/viator/viator-group/viator-group.component';
import { BookingHotelCardComponent } from 'src/app/shared/components/booking-hotel-card/booking-hotel-card.component';
import { HotelCardMobileComponent } from '../../countries/components/mobile/hotel-card-mobile/hotel-card-mobile.component';
import { OccasionGeniusCardComponent } from 'src/app/shared/components/occasion-genius-card/occasion-genius-card.component';
import { ViatorCardMobileComponent } from '../../countries/components/mobile/viator-card-mobile/viator-card-mobile.component';
import { WishPreviewCardMobileComponent } from '../../countries/components/mobile/wish-preview-card-mobile/wish-preview-card-mobile.component';
import { OccasionGeniusCardMobileComponent } from '../../countries/components/mobile/occasion-genius-card-mobile/occasion-genius-card-mobile.component';

const PARTNERS_LOGO_IMG: string[] = ['booking.png', 'viator.png'];

@Component({
    selector: 'app-user-wish-list-page',
    imports: [
        MatButtonModule,
        MatIconModule,
        GetFavoritesByProvider,
        OccasionGeniusCardComponent,
        BookingHotelCardComponent,
        ViatorGroupComponent,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        HeaderBannerComponent,
        TranslateModule,
        WishPreviewCardMobileComponent,
        HotelCardMobileComponent,
        ViatorCardMobileComponent,
        OccasionGeniusCardMobileComponent,
        SafeUrlPipe,
    ],
    templateUrl: './user-wish-list-page.component.html',
    styleUrl: './user-wish-list-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserWishListPageComponent implements OnInit {
  @Input() placeName: string = '';
  public contentBannerUrl = `${Config.imgCdnUrl}images/wish-list-page/wish-list-holets.jpg`;
  public partnersLogosUrl: string[] = [];

  public tripsCollection: WritableSignal<any[]> = signal(null);
  public selectedTrip: WritableSignal<any> = signal(null);
  public isFetching: WritableSignal<boolean> = signal(false);

  public eSvgIcons = ESvgIcons;
  public eRoutes = ERoutes;
  public placeProvider = CityProviders;
  public PIN_COLORS = PIN_COLORS;

  mobilePreviewCityIndex = -1;
  mobileCardCityPreviewIndex = -1;
  #tripsService: TripsService = inject(TripsService);
  #router: Router = inject(Router);
  #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  #headerStateService: HeaderStateService = inject(HeaderStateService);
  #favoritesProviderService: FavoritesProviderService = inject(
    FavoritesProviderService,
  );
  #tripWizardStateService: TripWizardStateService = inject(
    TripWizardStateService,
  );
  #cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  #schedulerBlockNavigationService = inject(SchedulerBlockNavigationService);

  constructor(
    protected breakpointService: BreakpointService,
    private userAccountService: UserAccountService,
    private cloudinaryService: CloudinaryService,
  ) {
    this.contentBannerUrl = this.cloudinaryService.getCloudImage(
      this.contentBannerUrl,
      true,
    );
    this.partnersLogosUrl = PARTNERS_LOGO_IMG.map((partnerPath) =>
      this.cloudinaryService.getCloudImage(
        `${Config.imgCdnUrl}images/${partnerPath}`,
      ),
    );
    this.#headerStateService.backBtnClickEmmit
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.#headerStateService.isBackBtnPresent = false;
        this.resetMobilePreview();
      });
  }

  selectMobilePreview(cityIndex: number) {
    this.userAccountService.setShowUserMobileSwitcher(false);
    this.#headerStateService.isBackBtnPresent = true;
    this.mobilePreviewCityIndex = cityIndex;
    this.mobileCardCityPreviewIndex = 0;
    this.#cdr.markForCheck();
  }

  selectCardCityPreview(cardIndex: number) {
    this.mobileCardCityPreviewIndex = cardIndex;
    this.#cdr.markForCheck();
  }

  resetMobilePreview() {
    this.userAccountService.setShowUserMobileSwitcher(true);
    this.mobileCardCityPreviewIndex = -1;
    this.mobilePreviewCityIndex = -1;
    this.#cdr.markForCheck();
  }

  backToTrip() {
    this.#router.navigate([ERoutes.SLASH, ERoutes.TRIP_PLANNER]);
  }

  public ngOnInit() {
    this.userAccountService.setShowUserMobileSwitcher(true);

    this.isFetching.set(true);
    const preselectedId = this.#activatedRoute.snapshot.queryParams.id;

    combineLatest<any>([
      this.#tripsService.getMyTrips(),
      this.#favoritesProviderService.getAllFavorites(),
    ]).subscribe({
      next: ([tripsData, favoritesData]) => {
        tripsData.trips.forEach((trip: any) => {
          trip.route.forEach((route: { id: number }, index: number) => {
            trip.preview[index].favorites = favoritesData.data.filter(
              (item: any) => item.place_id === route.id,
            );
            trip.preview[index].expanded = false;
            trip.preview[index].cityName = trip.route[index].destName;
            trip.preview[index].regionName = trip.route[index].region;
            trip.preview[index].countryName = trip.route[index].countryName;
            trip.preview[index].imageUrl = trip.route[index].imageUrl;
          });
        });
        this.tripsCollection.set(tripsData.trips);
        if (tripsData.tripsCount) {
          if (preselectedId) {
            const trip = this.tripsCollection().find(
              (i) => i.visible_id === preselectedId,
            );
            this.selectedTrip.set(trip);
          } else {
            this.selectedTrip.set(tripsData.trips[0]);
          }
        }
      },
      complete: () => this.isFetching.set(false),
    });
  }

  public setCurrentTrip(event: Event): void {
    const el = event.target as HTMLSelectElement;
    const index = el.value;
    this.selectedTrip.set(this.tripsCollection()[index]);
  }

  public removeFavoriteFromList(favoriteItem: IFavoriteItem, city): void {
    const index = city.favorites.indexOf(favoriteItem);
    city.favorites.splice(index, 1);
    city.favorites = city.favorites.slice();
  }

  deleteViatorFavourite(favoriteItem: IFavoriteItem, city: any): void {
    this.#favoritesProviderService.checkUserAuth(
      {
        favoriteId: favoriteItem.favorite_id,
        placeId: Number(favoriteItem.place_id),
        provider: 'viator',
      },
      favoriteItem.additionalData,
      this.placeName,
      (isDelete) => {
        if (isDelete) this.removeFavoriteFromList(favoriteItem, city);
        this.#cdr.markForCheck();
      },
    );
  }

  public browseTripPage(cityProvider: CityProviders, cityIndex: number): void {
    this.#tripWizardStateService.currentTripState =
      Trip.CreateTripBasedOnSavedTripModel(this.selectedTrip());
    this.#schedulerBlockNavigationService.scheduleNavigation(
      cityIndex,
      cityProvider,
    );
    this.#router.navigate(['trip-planner']);
  }
}
